import { useEffect, useState } from 'react';

import type { OnboardingPathWire } from './types';
import type { ReactElement } from 'react';

export interface ChoosePathResult {
  path: OnboardingPathWire;
  history?: { import: boolean; retentionMonths: 6 | 12 | 24 | 60 };
}

const CAMINHOS: { path: OnboardingPathWire; titulo: string; texto: string; precisa: string[] }[] = [
  {
    path: 'dedicated',
    titulo: 'Número novo ou dedicado',
    texto: 'Um chip novo, ou um número que nunca teve WhatsApp. É o caminho da empresa.',
    precisa: [
      'O chip no aparelho, para receber o código por SMS ou ligação',
      'Acesso à conta da empresa na Meta (ou criar uma na hora)',
    ],
  },
  {
    path: 'business_app',
    titulo: 'O número que uso no app do celular',
    texto: 'Você continua atendendo pelo WhatsApp Business no telefone, e a Lume atende junto.',
    precisa: [
      'O celular na mão, com o WhatsApp Business atualizado',
      'Abrir o app pelo menos a cada 14 dias — senão a conexão cai',
    ],
  },
  {
    path: 'migration',
    titulo: 'Trazer de outro sistema',
    texto: 'Seu número já atende pela API com outra empresa. A Meta move o número para cá.',
    precisa: [
      'Desligar a verificação em duas etapas no sistema antigo antes',
      'Empresa verificada na Meta',
      'O sistema antigo perde o número',
    ],
  },
];

/**
 * A2 — os três caminhos (spec §5). Cada cartão diz o que a pessoa precisa ter na mão ANTES de
 * abrir a Meta, para não descobrir no meio do popup. No caminho B, a pergunta do histórico (D4).
 */
export function ChoosePathDialog({
  onClose,
  onConfirm,
  pending,
}: {
  onClose: () => void;
  onConfirm: (r: ChoosePathResult) => void;
  pending: boolean;
}): ReactElement {
  const [path, setPath] = useState<OnboardingPathWire | null>(null);
  const [importar, setImportar] = useState(true);
  const [retencao, setRetencao] = useState<6 | 12 | 24 | 60>(24);

  // Escape fecha (revisão de 30/Set, menor 8) — menos no meio do envio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, pending]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start justify-center overflow-auto bg-black/50 p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="choose-path-title"
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-surface shadow-lg">
        <div className="px-6 pt-5">
          <h2 id="choose-path-title" className="text-base font-semibold text-text-primary">
            Como você quer conectar o WhatsApp?
          </h2>
          <p className="mt-0.5 text-xs text-text-secondary">
            Os três caminhos terminam no mesmo lugar: seu número atendendo pela Lume. O que muda é o
            que a Meta vai pedir.
          </p>
        </div>
        <div className="px-6 py-4">
          <div className="grid gap-2.5 sm:grid-cols-3" role="radiogroup" aria-label="Caminho">
            {CAMINHOS.map((c) => (
              <button
                key={c.path}
                type="button"
                role="radio"
                aria-checked={path === c.path}
                onClick={() => {
                  setPath(c.path);
                }}
                className={`flex flex-col gap-2 rounded-lg border p-3.5 text-left ${
                  path === c.path
                    ? 'border-accent ring-[3px] ring-accent/15'
                    : 'border-border-default'
                }`}
              >
                <span className="text-[13px] font-semibold text-text-primary">{c.titulo}</span>
                <span className="text-xs leading-snug text-text-secondary">{c.texto}</span>
                <ul className="list-disc pl-3 text-[11px] leading-relaxed text-text-secondary">
                  {c.precisa.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          {path === 'business_app' && (
            <fieldset className="mt-3.5 rounded-lg border border-border-default bg-bg-subtle p-3.5">
              <legend className="px-1 text-xs font-semibold text-text-primary">
                Trazer as conversas e os contatos do celular para a Lume?
              </legend>
              <label className="flex items-start gap-2 text-xs text-text-secondary">
                <input
                  type="radio"
                  name="history"
                  checked={importar}
                  onChange={() => {
                    setImportar(true);
                  }}
                />
                <span>
                  <strong>Sim, trazer.</strong> Os últimos 6 meses de conversas e a agenda do app
                  aparecem aqui, e a Lume passa a conhecer seus clientes desde o primeiro dia.
                </span>
              </label>
              <label className="mt-1.5 flex items-start gap-2 text-xs text-text-secondary">
                <input
                  type="radio"
                  name="history"
                  checked={!importar}
                  onChange={() => {
                    setImportar(false);
                  }}
                />
                <span>
                  <strong>Não, começar do zero.</strong> Só as conversas novas entram na Lume. Os
                  contatos ficam no celular.
                </span>
              </label>
              {/* Prazo de guarda só existe para o que é trazido (revisão de 30/Set, menor 8). */}
              {importar && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
                  <label htmlFor="retencao">Guardar o histórico trazido por</label>
                  <select
                    id="retencao"
                    value={retencao}
                    onChange={(e) => {
                      setRetencao(Number(e.target.value) as 6 | 12 | 24 | 60);
                    }}
                    className="rounded-md border border-border-strong bg-surface px-2 py-1 text-xs text-text-primary"
                  >
                    <option value={6}>6 meses</option>
                    <option value={12}>1 ano</option>
                    <option value={24}>2 anos</option>
                    <option value={60}>5 anos</option>
                  </select>
                  <span className="text-[11px]">Você pode mudar depois, em Privacidade.</span>
                </div>
              )}
            </fieldset>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border-default bg-bg-subtle px-6 py-3.5">
          <span className="mr-auto text-[11px] text-text-secondary">
            Vai abrir uma janela da Meta. Termine lá e volte para cá.
          </span>
          <button
            type="button"
            className="rounded-md border border-border-strong bg-surface px-3 py-1.5 text-xs text-text-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={path === null || pending}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            onClick={() => {
              if (path === null) return;
              onConfirm(
                path === 'business_app'
                  ? { path, history: { import: importar, retentionMonths: retencao } }
                  : { path },
              );
            }}
          >
            {pending ? 'Abrindo…' : 'Continuar na Meta'}
          </button>
        </div>
      </div>
    </div>
  );
}
